import React, { FC, useCallback, useMemo, useState } from 'react'
import { Animated, Pressable, View } from 'react-native'

import formatDecimals from '@ambire-common/utils/formatDecimals/formatDecimals'
import SkeletonLoader from '@common/components/SkeletonLoader'
import Text from '@common/components/Text'
import { useTranslation } from '@common/config/localization'
import useTheme from '@common/hooks/useTheme'
import DashboardHeader from '@common/modules/dashboard/components/DashboardHeader'
import Routes from '@common/modules/dashboard/components/Routes'
import { PrivateModeReason } from '@web/utils/privateModeAvailability'
import useBalanceAffectingErrors from '@common/modules/dashboard/hooks/useBalanceAffectingErrors'
import { useDashboardPrivatePendingRow } from '@common/modules/dashboard/hooks/useDashboardPrivatePendingRow'
import { useRailgunOverviewBalance } from '@common/modules/dashboard/hooks/useRailgunOverviewBalance'
import { useRailgunShieldPending } from '@common/modules/dashboard/hooks/useRailgunShieldPending'
import { OVERVIEW_CONTENT_MAX_HEIGHT } from '@common/modules/dashboard/screens/DashboardScreen'
import spacings, { SPACING, SPACING_SM, SPACING_TY, SPACING_XL } from '@common/styles/spacings'
import common from '@common/styles/utils/common'
import flexbox from '@common/styles/utils/flexbox'
import useBackgroundService from '@web/hooks/useBackgroundService'
import useHover, { AnimatedPressable } from '@web/hooks/useHover'
import useMainControllerState from '@web/hooks/useMainControllerState'
import useNetworksControllerState from '@web/hooks/useNetworksControllerState'
import useSelectedAccountControllerState from '@web/hooks/useSelectedAccountControllerState'
import { FEATURES } from '@web/constants/features'

import GasTankButton from '../DashboardHeader/GasTankButton'
import BalanceAffectingErrors from './BalanceAffectingErrors'
import BatchOrFirstBalanceHint from './BatchOrFirstBalanceHint'
import RailgunMissingPoiUsdPill from './RailgunMissingPoiUsdPill/RailgunMissingPoiUsdPill'
import { RailgunShieldPendingBadge } from './RailgunShieldPendingBadge'
import RefreshIcon from './RefreshIcon'
import getStyles from './styles'
import useRailgunContext from '@web/hooks/useRailgunContext'
import { ModeEnum, Mode } from '@web/contexts/appModeContext'
import { RailgunStatusBadge, useRailgunStatusBadgeData } from '@web/components/RailgunStatusBadge'
import { useRailgunMerkletreeScanContext } from '@web/contexts/railgunContext'
import GrainientBanner from '@common/modules/dashboard/home-kit/GrainientBanner'
import { EyeIcon, EyeOffIcon } from '@common/modules/dashboard/home-kit/HomeIcons'
import { RADII } from '@common/modules/dashboard/home-kit/tokens'

const THRESHOLD_AMOUNT_TO_HIDE_BALANCE_DECIMALS = 10000

// All text/icons sit on the animated gradient banner, so we use a light palette
// (white + translucent white) rather than the theme's dark-on-light tokens.
const ON_BANNER = '#fff'
const ON_BANNER_SOFT = '#fff'

interface Props {
  mode: Mode
  openGasTankModal: () => void
  animatedOverviewHeight: Animated.Value
  dashboardOverviewSize: {
    width: number
    height: number
  }
  setDashboardOverviewSize: React.Dispatch<React.SetStateAction<{ width: number; height: number }>>
  privateModeReason: PrivateModeReason
}

// We create a reusable height constant for both the Balance amount height and the Balance skeleton.
// We want both components to have the same height; otherwise, clicking on the RefreshIcon causes a layout shift.
const BALANCE_HEIGHT = 38

const DashboardOverview: FC<Props> = ({
  mode,
  openGasTankModal,
  animatedOverviewHeight,
  // dashboardOverviewSize,
  setDashboardOverviewSize,
  privateModeReason
}) => {
  const { dispatch } = useBackgroundService()
  const { t } = useTranslation()
  const { theme, styles } = useTheme(getStyles)
  const { isOffline } = useMainControllerState()
  const { account, dashboardNetworkFilter, portfolio } = useSelectedAccountControllerState()
  const railgunOverview = useRailgunOverviewBalance(mode)
  const {
    refreshPrivateHistory,
    isBalanceFetching: isPrivateBalanceFetching,
    allRailgunWallets
  } = useRailgunContext()
  const hasRailgunWallet = useMemo(() => {
    return allRailgunWallets.some((wallet) => wallet.accountAddr === account?.addr)
  }, [allRailgunWallets, account?.addr])
  const shieldPending = useRailgunShieldPending(mode)
  const shouldShowBatchOrHint =
    mode === ModeEnum.PRIVATE && railgunOverview.isRailgunBalanceFetching

  // No global hide-balance preference exists in the codebase, so mask the number
  // locally (mirrors the mobile-web BalanceCard's `hidden` state). Toggled by the
  // eye affordance next to the "Estimated Balance" label.
  const [balanceHidden, setBalanceHidden] = useState(false)

  const [bindRefreshButtonAnim, refreshButtonAnimStyle] = useHover({
    preset: 'opacity'
  })

  const { scanningProgress } = useRailgunMerkletreeScanContext()

  const {
    sheetRef,
    balanceAffectingErrorsSnapshot,
    warningMessage,
    onIconPress,
    closeBottomSheetWrapped,
    isLoadingTakingTooLong,
    networksWithErrors
  } = useBalanceAffectingErrors()

  const totalPortfolioAmount = useMemo(() => portfolio?.totalBalance || 0, [portfolio])

  const [totalPortfolioAmountIntegerFormattedPart, totalPortfolioAmountDecimalFormattedPart] =
    formatDecimals(totalPortfolioAmount, 'value').split('.')

  const isFetchingBalance = useMemo(() => {
    if (mode === ModeEnum.PUBLIC) {
      return !portfolio?.isAllReady || portfolio.isReloading
    }
    return railgunOverview.isRailgunBalanceFetching
  }, [
    mode,
    portfolio?.isAllReady,
    portfolio?.isReloading,
    railgunOverview.isRailgunBalanceFetching
  ])

  const showBalanceSkeleton = useMemo(() => {
    if (mode === ModeEnum.PUBLIC) return !portfolio?.isReadyToVisualize
    // Snapshot-first: keep the prior balance visible during refresh instead of replacing it
    // with a skeleton. Phase 0.4 of RAILGUN_HYBRID_REWRITE_PLAN.md.
    if (railgunOverview.hasPriorBalance) return false
    return railgunOverview.isRailgunBalanceFetching
  }, [
    mode,
    portfolio?.isReadyToVisualize,
    railgunOverview.isRailgunBalanceFetching,
    railgunOverview.hasPriorBalance
  ])

  const balanceAmountToDisplay = useMemo(() => {
    if (mode === ModeEnum.PUBLIC) {
      return {
        integer: totalPortfolioAmountIntegerFormattedPart,
        decimal: totalPortfolioAmountDecimalFormattedPart
      }
    }
    return railgunOverview.totalRailgunBalanceFormatted
  }, [
    mode,
    totalPortfolioAmountIntegerFormattedPart,
    totalPortfolioAmountDecimalFormattedPart,
    railgunOverview.totalRailgunBalanceFormatted
  ])

  const amountForDecimalThreshold =
    mode === ModeEnum.PUBLIC ? totalPortfolioAmount : railgunOverview.totalRailgunBalanceForDisplay

  const showGasTank = mode === ModeEnum.PUBLIC && FEATURES.gasTankUiEnabled

  const { totalMissingPoiUsd, showPrivatePendingRow } = useDashboardPrivatePendingRow({
    mode,
    shouldShowBatchOrHint,
    isRailgunBalanceFetching: railgunOverview.isRailgunBalanceFetching,
    totalMissingInternalPOIUSD: railgunOverview.totalMissingInternalPOIUSD,
    totalMissingExternalPOIUSD: railgunOverview.totalMissingExternalPOIUSD,
    shieldPending
  })

  const reloadAccount = useCallback(() => {
    if (mode === ModeEnum.PUBLIC) {
      dispatch({
        type: 'MAIN_CONTROLLER_RELOAD_SELECTED_ACCOUNT',
        params: {
          chainId: dashboardNetworkFilter ?? undefined
        }
      })
    } else {
      void railgunOverview.fetchRailgunBalance()
      void refreshPrivateHistory({ force: true })
    }
  }, [dashboardNetworkFilter, dispatch, mode, railgunOverview, refreshPrivateHistory])

  // Orange "balance may be inaccurate" tint only applies to the PUBLIC portfolio, whose
  // balance is affected by per-chain fetch errors / offline state. The PRIVATE (RAILGUN)
  // balance comes from a different source, so public-chain errors must not recolor it.
  const balanceColor =
    mode === ModeEnum.PUBLIC && (networksWithErrors.length || isOffline)
      ? theme.warningDecorative2
      : ON_BANNER

  return (
    <View style={[spacings.phSm, spacings.mbMi]}>
      <View
        style={[
          styles.contentContainer,
          {
            // home-v2: banner corners are 16 (RADII.lg), matching the holdings card.
            borderRadius: RADII.lg,
            position: 'relative',
            overflow: 'hidden',
            borderWidth: 1,
            borderColor: 'rgba(255,255,255,0.12)'
          }
        ]}
      >
        {/* animated grain-gradient banner (public = blue, private = orange).
            Use the SAME radius as the outer container so the gradient corners line
            up with the container's rounded corners (the container is clipped with
            overflow:'hidden'). */}
        <GrainientBanner mode={mode} borderRadius={RADII.lg} />

        <Animated.View
          style={[
            common.borderRadiusPrimary,
            spacings.ptTy,
            spacings.phSm,
            {
              zIndex: 1,
              paddingBottom: animatedOverviewHeight.interpolate({
                inputRange: [0, OVERVIEW_CONTENT_MAX_HEIGHT],
                outputRange: [SPACING_TY, SPACING_SM],
                extrapolate: 'clamp'
              }),
              overflow: 'hidden'
            }
          ]}
          onLayout={(e) => {
            setDashboardOverviewSize({
              width: e.nativeEvent.layout.width,
              height: e.nativeEvent.layout.height
            })
          }}
        >
          <View style={{ zIndex: 2 }}>
            <DashboardHeader />
            <Animated.View
              style={{
                ...styles.overview,
                paddingTop: animatedOverviewHeight.interpolate({
                  inputRange: [0, SPACING_XL],
                  outputRange: [0, SPACING],
                  extrapolate: 'clamp'
                }),
                maxHeight: animatedOverviewHeight,
                overflow: 'hidden'
              }}
            >
              <View>
                {/* "Estimated Balance" label + hide/show eye affordance */}
                <View
                  style={[flexbox.directionRow, flexbox.alignCenter, spacings.mtMi, { gap: 8 }]}
                >
                  <Text fontSize={14} weight="semiBold" color={ON_BANNER_SOFT}>
                    {t('Estimated Balance')}
                  </Text>
                  <Pressable
                    onPress={() => setBalanceHidden((v) => !v)}
                    hitSlop={8}
                    testID="dashboard-hide-balance"
                  >
                    {balanceHidden ? (
                      <EyeOffIcon size={16} color={ON_BANNER_SOFT} />
                    ) : (
                      <EyeIcon size={16} color={ON_BANNER_SOFT} />
                    )}
                  </Pressable>
                </View>

                <View
                  style={[
                    flexbox.directionRow,
                    flexbox.alignCenter,
                    spacings.mbTy,
                    { height: BALANCE_HEIGHT }
                  ]}
                >
                  {showBalanceSkeleton ? (
                    <SkeletonLoader
                      lowOpacity
                      width={180}
                      height={BALANCE_HEIGHT}
                      borderRadius={8}
                    />
                  ) : (
                    <Pressable
                      onPress={onIconPress}
                      disabled={!warningMessage || isLoadingTakingTooLong || isOffline}
                      testID="full-balance"
                      style={[flexbox.directionRow, flexbox.alignCenter]}
                    >
                      {balanceHidden ? (
                        <Text
                          fontSize={32}
                          shouldScale={false}
                          weight="number_bold"
                          style={{ lineHeight: 28 }}
                          color={balanceColor}
                          selectable
                        >
                          ••••••••
                        </Text>
                      ) : (
                        <Text selectable>
                          <Text
                            fontSize={32}
                            shouldScale={false}
                            weight="number_bold"
                            // Line height should be constant based on font size, not on parent height
                            style={{ lineHeight: 28 }}
                            color={balanceColor}
                            selectable
                            testID="total-portfolio-amount-integer"
                          >
                            {balanceAmountToDisplay.integer}
                          </Text>
                          {amountForDecimalThreshold < THRESHOLD_AMOUNT_TO_HIDE_BALANCE_DECIMALS && (
                            <Text
                              fontSize={20}
                              shouldScale={false}
                              weight="number_bold"
                              color={balanceColor}
                              selectable
                            >
                              {t('.')}
                              {balanceAmountToDisplay.decimal}
                            </Text>
                          )}
                        </Text>
                      )}
                    </Pressable>
                  )}
                  <View
                    style={[flexbox.directionRow, flexbox.alignCenter, spacings.mlTy, { gap: 8 }]}
                  >
                    <AnimatedPressable
                      style={refreshButtonAnimStyle}
                      onPress={reloadAccount}
                      {...bindRefreshButtonAnim}
                      disabled={isFetchingBalance}
                      testID="refresh-button"
                    >
                      <RefreshIcon
                        spin={isFetchingBalance}
                        color={ON_BANNER}
                        width={16}
                        height={16}
                      />
                    </AnimatedPressable>
                    {mode === ModeEnum.PRIVATE && isPrivateBalanceFetching && (
                      <Text
                        fontSize={14}
                        weight="number_bold"
                        color={ON_BANNER}
                        testID="private-merkletree-scan-progress"
                      >
                        {scanningProgress}
                      </Text>
                    )}
                  </View>
                </View>

                <View style={{ flexDirection: 'column', alignItems: 'flex-start', minHeight: 28 }}>
                  <View style={[flexbox.directionRow, flexbox.alignCenter]}>
                    {showGasTank && (
                      <GasTankButton
                        onPress={openGasTankModal}
                        portfolio={portfolio}
                        account={account}
                      />
                    )}
                    {showPrivatePendingRow && (
                      <View style={[flexbox.directionRow, flexbox.alignCenter]}>
                        {(shieldPending.processingMultiCount != null ||
                          shieldPending.badgeText != null) && (
                          <RailgunShieldPendingBadge
                            processingMultiCount={shieldPending.processingMultiCount}
                            badgeText={shieldPending.badgeText}
                          />
                        )}
                        <RailgunMissingPoiUsdPill usdTotal={totalMissingPoiUsd} />
                      </View>
                    )}
                    {mode === ModeEnum.PUBLIC && (
                      <BalanceAffectingErrors
                        reloadAccount={reloadAccount}
                        networksWithErrors={networksWithErrors}
                        sheetRef={sheetRef}
                        balanceAffectingErrorsSnapshot={balanceAffectingErrorsSnapshot}
                        warningMessage={warningMessage}
                        onIconPress={onIconPress}
                        closeBottomSheetWrapped={closeBottomSheetWrapped}
                        isLoadingTakingTooLong={isLoadingTakingTooLong}
                      />
                    )}
                    {shouldShowBatchOrHint && <BatchOrFirstBalanceHint mode={mode} />}
                    {mode === ModeEnum.PRIVATE && railgunOverview.cachePoisoned && (
                      <View
                        style={[
                          spacings.mtTy,
                          spacings.phSm,
                          spacings.pvMi,
                          common.borderRadiusPrimary,
                          {
                            backgroundColor: theme.errorBackground,
                            alignSelf: 'flex-start',
                            maxWidth: 320
                          }
                        ]}
                      >
                        <Text fontSize={11} weight="medium" color={theme.errorText}>
                          {`Local cache out of sync${
                            railgunOverview.cachePoisonedNetworks.length
                              ? ` (${railgunOverview.cachePoisonedNetworks.join(', ')})`
                              : ''
                          }. Spends are blocked until the next sync verifies.`}
                        </Text>
                      </View>
                    )}
                  </View>
                </View>
              </View>
              <Routes hasRailgunWallet={hasRailgunWallet} privateModeReason={privateModeReason} />
            </Animated.View>
          </View>
        </Animated.View>
      </View>
    </View>
  )
}

const RailgunStatusBadgeMount: FC = () => {
  const data = useRailgunStatusBadgeData()
  const { networks } = useNetworksControllerState()
  // Map chainId → human network name so the badge reads "Updating Ethereum…" instead of the raw
  // "Updating chain 1…" fallback. Falls back to `chain N` only for ids not in the network list.
  const getChainName = useCallback(
    (chainId: number) => {
      const network = networks?.find((n) => Number(n.chainId) === chainId)
      return network?.name ?? `chain ${chainId}`
    },
    [networks]
  )
  if (!data) return null
  // Quiet UX: when everything is verified the badge has nothing to say, so we
  // hide it instead of leaving a permanent green pill in the header. It will
  // re-appear automatically when the SW transitions the snapshot to
  // syncing/partial/stale/error/poisoned (e.g. after the user clicks reload).
  if (data.status === 'verified') return null
  return (
    <View style={{ alignSelf: 'center', marginLeft: 8 }}>
      <RailgunStatusBadge
        status={data.status}
        refreshedAt={data.refreshedAt}
        coverage={data.coverage}
        getChainName={getChainName}
      />
    </View>
  )
}

export default React.memo(DashboardOverview)
